<template>
  <div class="ap-page">
    <v-snackbar v-model="showSnackBar" top right :timeout="4000">
      {{ msg }}
      <template v-slot:action="{ attrs }">
        <v-btn icon small v-bind="attrs" @click="showSnackBar = false">
          <v-icon small color="white">mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>
    <vue-element-loading :active="busy" :is-full-screen="true" background-color="rgba(255,255,255,0.7)" color="#16a34a" spinner="spinner" />

    <div class="list-toolbar">
      <nav class="ap-tabs" aria-label="Application lists">
        <router-link v-for="t in tabs" :key="t.mode" :to="t.route" class="ap-tab" :class="{ 'ap-tab--active': t.mode === mode }">
          <v-icon small>{{ t.icon }}</v-icon>
          {{ t.label }}
        </router-link>
      </nav>
      <div class="list-toolbar-actions">
        <router-link to="/trackapplication" class="ap-btn ap-btn--outline">
          <v-icon small>mdi-map-marker-path</v-icon>
          Track an application
        </router-link>
        <router-link to="/booknow" class="ap-btn ap-btn--primary">
          <v-icon small>mdi-plus</v-icon>
          New application
        </router-link>
      </div>
    </div>

    <!-- Filters -->
    <div v-if="config.filters" class="ap-card">
      <div class="ap-card-body list-filters">
        <v-text-field
          v-model="keyword"
          outlined
          dense
          hide-details
          clearable
          prepend-inner-icon="mdi-magnify"
          label="Application number"
          @keyup.enter="applyFilters"
          @click:clear="keyword = ''; applyFilters()"
        ></v-text-field>
        <v-menu v-model="fromMenu" :close-on-content-click="false" max-width="290">
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              :value="fromDate"
              outlined
              dense
              hide-details
              readonly
              clearable
              label="From date"
              prepend-inner-icon="mdi-calendar-start"
              v-bind="attrs"
              v-on="on"
              @click:clear="fromDate = null; toDate = null; applyFilters()"
            ></v-text-field>
          </template>
          <v-date-picker v-model="fromDate" color="#16a34a" @change="fromMenu = false; applyFilters()"></v-date-picker>
        </v-menu>
        <v-menu v-model="toMenu" :close-on-content-click="false" max-width="290">
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              :value="toDate"
              outlined
              dense
              hide-details
              readonly
              clearable
              :disabled="!fromDate"
              label="To date"
              prepend-inner-icon="mdi-calendar-end"
              v-bind="attrs"
              v-on="on"
              @click:clear="toDate = null; applyFilters()"
            ></v-text-field>
          </template>
          <v-date-picker v-model="toDate" :min="fromDate" color="#16a34a" @change="toMenu = false; applyFilters()"></v-date-picker>
        </v-menu>
      </div>
    </div>

    <div class="ap-card list-card">
      <div v-if="loading" class="pa-5">
        <v-skeleton-loader type="table-thead, table-row@5"></v-skeleton-loader>
      </div>

      <div v-else-if="!list.length" class="ap-empty">
        <span class="ap-empty-icon"><v-icon size="34">{{ config.emptyIcon }}</v-icon></span>
        <div class="ap-empty-title">{{ config.emptyTitle }}</div>
        <div>{{ config.emptyText }}</div>
        <router-link v-if="mode === 'mine'" to="/booknow" class="ap-btn ap-btn--primary mt-2">
          <v-icon small>mdi-plus</v-icon>
          Start a new application
        </router-link>
      </div>

      <div v-else class="list-scroll">
        <table class="list-table">
          <thead>
            <tr>
              <th>Application</th>
              <th>Production</th>
              <th>Location &amp; dates</th>
              <th class="list-num">Fees</th>
              <th>Status</th>
              <th>Stage</th>
              <th class="list-actions-head">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in list" :key="item._id">
              <td data-label="Application">
                <div>
                  <div class="list-no">{{ item.applicationNo || "—" }}</div>
                  <div class="list-sub">Applied {{ formatDate(item.created_at) }}</div>
                  <div v-if="item.isExtended && item.originalApplication && item.originalApplication.applicationNo" class="list-ref">
                    <v-icon x-small>mdi-calendar-expand-horizontal</v-icon>
                    Extension of {{ item.originalApplication.applicationNo }}
                  </div>
                </div>
              </td>
              <td data-label="Production">
                <div>
                  <div class="list-title">{{ filmName(item) }}</div>
                  <div class="list-sub">{{ item.natureOfApplication || "—" }}</div>
                </div>
              </td>
              <td data-label="Location & dates">
                <div>
                  <div class="list-strong">{{ firstLocation(item) || "—" }}</div>
                  <div v-if="dateRange(item)" class="list-sub">{{ dateRange(item) }}</div>
                </div>
              </td>
              <td data-label="Fees" class="list-num">
                <span v-if="item.shootingfee || item.installationfee" class="list-fee">₹{{ money((item.shootingfee || 0) + (item.installationfee || 0)) }}</span>
                <span v-else class="list-sub">Not fixed yet</span>
              </td>
              <td data-label="Status">
                <div class="list-status">
                  <span class="ap-pill" :class="'ap-pill--' + statusOf(item).tone">{{ statusOf(item).label }}</span>
                  <span v-if="mode === 'forwarded' && item.reason && item.reason !== 'Nill'" class="list-note list-note--warn" :title="item.reason">
                    {{ item.reason }}
                  </span>
                </div>
              </td>
              <td data-label="Stage">
                <!-- Rejected and licensed applications show how they ended, not who has them -->
                <div v-if="outcomeOf(item)" class="outcome" :class="'outcome--' + outcomeOf(item).kind">
                  <span class="outcome-icon"><v-icon small>{{ outcomeOf(item).icon }}</v-icon></span>
                  <div class="outcome-text">
                    <div class="outcome-title">{{ outcomeOf(item).title }}</div>
                    <div v-if="outcomeOf(item).when" class="outcome-when">{{ formatDateTime(outcomeOf(item).when) }}</div>
                    <div v-if="outcomeOf(item).note" class="outcome-note" :title="outcomeOf(item).note">{{ outcomeOf(item).note }}</div>
                    <div v-if="outcomeOf(item).flag" class="outcome-flag">
                      <v-icon x-small>mdi-timer-sand</v-icon>
                      {{ outcomeOf(item).flag }}
                    </div>
                  </div>
                </div>
                <div v-else class="where" :class="'where--' + whereIs(item).tone">
                  <span class="where-icon"><v-icon small>{{ whereIs(item).icon }}</v-icon></span>
                  <div class="where-text">
                    <div class="where-who">{{ whereIs(item).who }}</div>
                    <div v-if="whereIs(item).detail" class="where-detail">{{ whereIs(item).detail }}</div>
                  </div>
                </div>
              </td>
              <td class="list-actions">
                <div class="list-actions-row">
                  <button v-if="mode === 'forwarded'" type="button" class="ap-btn ap-btn--gold ap-btn--sm" @click="$router.push('/editbooking?id=' + item._id)">
                    <v-icon small>mdi-pencil-outline</v-icon>
                    Edit
                  </button>
                  <button v-if="mode === 'mine' && canExtend(item)" type="button" class="ap-btn ap-btn--gold ap-btn--sm" @click="openExtend(item)">
                    <v-icon small>mdi-calendar-expand-horizontal</v-icon>
                    Extend
                  </button>
                  <button v-if="item.status === 'Rejected'" type="button" class="list-icon-btn" title="Rejection order" aria-label="Rejection order" @click="pdf('/rejectby/divsion/pdf', item._id)">
                    <v-icon small>mdi-file-document-outline</v-icon>
                  </button>
                  <button v-if="mode === 'cancelled' && item.status === 'Cancelled'" type="button" class="list-icon-btn" title="Cancellation order" aria-label="Cancellation order" @click="pdf('/cancelby/applicant/pdf', item._id)">
                    <v-icon small>mdi-file-document-outline</v-icon>
                  </button>
                  <button type="button" class="ap-btn ap-btn--primary ap-btn--sm" @click="view(item)">
                    Open
                    <v-icon small>mdi-arrow-right</v-icon>
                  </button>
                </div>
                <div v-if="hasDocs(item)" class="list-docs">
                  <a v-if="item.license" :href="fileUrl(item.license)" target="_blank" rel="noopener" class="list-doc">
                    <v-icon x-small>mdi-file-certificate-outline</v-icon>
                    Shooting licence
                    <v-icon x-small>mdi-download</v-icon>
                  </a>
                  <a v-if="item.proceedings" :href="fileUrl(item.proceedings)" target="_blank" rel="noopener" class="list-doc">
                    <v-icon x-small>mdi-gavel</v-icon>
                    Proceedings
                    <v-icon x-small>mdi-download</v-icon>
                  </a>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div v-if="pages > 1" class="list-pager">
      <v-pagination v-model="currentPage" :length="pages" :total-visible="7" color="#16a34a"></v-pagination>
    </div>

    <!-- Extension request -->
    <v-dialog v-model="extendDialog" max-width="980" persistent scrollable>
      <v-card class="ap-dialog">
        <div class="ap-dialog-head">
          <h2 class="ap-dialog-title">Request an extension</h2>
          <p class="ap-dialog-sub">Application No. {{ extendApp && extendApp.applicationNo }} — update the locations and dates you need.</p>
        </div>
        <v-card-text class="ap-page ap-dialog-body">
          <div v-for="(row, i) in extendRows" :key="i" class="ap-block">
            <div class="ap-block-head">
              <span class="ap-block-title">Location {{ i + 1 }}</span>
              <button v-if="extendRows.length > 1" type="button" class="ap-btn ap-btn--danger ap-btn--sm" @click="extendRows.splice(i, 1)">
                <v-icon small>mdi-delete-outline</v-icon>
                Remove
              </button>
            </div>
            <div class="ap-grid">
              <v-text-field v-model="row.location" outlined dense hide-details="auto" label="Location"></v-text-field>
              <v-text-field v-model="row.filmName" outlined dense hide-details="auto" label="Film name"></v-text-field>
              <v-menu v-model="row.menuStart" :close-on-content-click="false" max-width="290">
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field :value="row.fromDate" outlined dense readonly hide-details="auto" label="From date" prepend-inner-icon="mdi-calendar-start" v-bind="attrs" v-on="on"></v-text-field>
                </template>
                <v-date-picker v-model="row.fromDate" :min="today" color="#16a34a" @change="row.menuStart = false"></v-date-picker>
              </v-menu>
              <v-menu v-model="row.menuEnd" :close-on-content-click="false" max-width="290">
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field :value="row.toDate" outlined dense readonly hide-details="auto" label="To date" prepend-inner-icon="mdi-calendar-end" v-bind="attrs" v-on="on"></v-text-field>
                </template>
                <v-date-picker v-model="row.toDate" :min="row.fromDate || today" color="#16a34a" @change="row.menuEnd = false"></v-date-picker>
              </v-menu>
              <v-text-field v-model="row.noOfMembers" type="number" min="1" outlined dense hide-details="auto" label="Crew members"></v-text-field>
              <v-text-field v-model="row.noOfVehicles" type="number" min="0" outlined dense hide-details="auto" label="Vehicles"></v-text-field>
              <v-text-field v-model="row.installationDays" type="number" min="0" outlined dense hide-details="auto" label="Installation days"></v-text-field>
            </div>
          </div>
          <button type="button" class="ap-btn ap-btn--outline ap-btn--sm" @click="addExtendRow">
            <v-icon small>mdi-plus</v-icon>
            Add location
          </button>
        </v-card-text>
        <div class="ap-dialog-actions">
          <button type="button" class="ap-btn ap-btn--outline" @click="extendDialog = false">Cancel</button>
          <button type="button" class="ap-btn ap-btn--primary" :disabled="busy" @click="submitExtend">
            <v-icon small>mdi-send-outline</v-icon>
            Submit request
          </button>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from "axios";
import moment from "moment";
import { canExtend, isLicensed, outcomeOf, statusOf, whereIs, openPdf } from "@/components/Common/applicantConfig";

const PAGE_SIZE = 10;

const MODES = {
  mine: {
    url: "/my/applications",
    body: { status: "yes" },
    filters: true,
    emptyIcon: "mdi-movie-open-outline",
    emptyTitle: "No applications yet",
    emptyText: "Apply for your first film shooting permit to see it here.",
  },
  forwarded: {
    url: "/forwarded/apps",
    body: { task: "T0", status: "yes" },
    filters: true,
    emptyIcon: "mdi-check-all",
    emptyTitle: "Nothing to fix",
    emptyText: "No applications have been returned to you.",
  },
  // The two views below are taken from the applicant's own applications
  // (a handful per person), so they are filtered and paged here.
  licence: {
    url: "/my/applications",
    body: { status: "yes" },
    filters: true,
    clientFilter: isLicensed,
    emptyIcon: "mdi-file-certificate-outline",
    emptyTitle: "No licences issued yet",
    emptyText: "Approved applications with their shooting licence and proceedings order appear here.",
  },
  rejected: {
    url: "/my/applications",
    body: { status: "yes" },
    filters: true,
    clientFilter: (a) => a.status === "Rejected",
    emptyIcon: "mdi-close-octagon-outline",
    emptyTitle: "No rejected applications",
    emptyText: "Applications the division office rejects will appear here with their rejection order.",
  },
  cancelled: {
    url: "/my/cancelled/applications",
    body: { status: "yes" },
    filters: false,
    emptyIcon: "mdi-cancel",
    emptyTitle: "No cancelled applications",
    emptyText: "Applications you cancel will appear here.",
  },
};

// The applicant's application lists (My Applications, Returned to me,
// Cancelled) — one page, three modes.
export default {
  name: "ApplicantApplications",
  props: {
    mode: { type: String, default: "mine", validator: (v) => !!MODES[v] },
  },
  data() {
    return {
      list: [],
      loading: false,
      busy: false,
      currentPage: 1,
      totalData: 0,
      pages: 0,
      keyword: "",
      fromDate: null,
      toDate: null,
      fromMenu: false,
      toMenu: false,
      msg: "",
      showSnackBar: false,
      extendDialog: false,
      extendApp: null,
      extendRows: [],
      today: moment().format("YYYY-MM-DD"),
      // Every application of this applicant, for the views that filter here
      allItems: [],
      tabs: [
        { mode: "mine", label: "My applications", icon: "mdi-file-document-multiple-outline", route: "/mybooking" },
        { mode: "licence", label: "Licence issued", icon: "mdi-file-certificate-outline", route: "/licenceissued" },
        { mode: "forwarded", label: "Returned to me", icon: "mdi-reply-outline", route: "/ForwardedApplications" },
        { mode: "rejected", label: "Rejected", icon: "mdi-close-octagon-outline", route: "/rejectedapplications" },
        { mode: "cancelled", label: "Cancelled", icon: "mdi-cancel", route: "/cancelledapplications" },
      ],
    };
  },
  computed: {
    config() {
      return MODES[this.mode];
    },
  },
  watch: {
    currentPage() {
      if (this.config.clientFilter) this.showPage();
      else this.getList();
    },
  },
  mounted() {
    this.getList();
  },
  methods: {
    statusOf,
    whereIs,
    outcomeOf,
    canExtend,
    toast(text) {
      this.msg = text;
      this.showSnackBar = true;
    },
    applyFilters() {
      if (this.currentPage !== 1) this.currentPage = 1;
      else this.getList();
    },
    getList() {
      this.loading = true;
      // A filtered view needs every application, then pages them itself
      const clientFilter = this.config.clientFilter;
      const body = { ...this.config.body, page: clientFilter ? 1 : this.currentPage, limit: clientFilter ? 500 : PAGE_SIZE };
      if (this.config.filters) {
        if (this.fromDate) body.fromDate = new Date(this.fromDate).toISOString();
        if (this.toDate) body.toDate = new Date(this.toDate).toISOString();
        if (this.keyword) body.applicationNo = this.keyword.trim();
      }
      axios({ method: "POST", url: this.config.url, headers: { token: localStorage.getItem("token") }, data: body })
        .then((response) => {
          this.loading = false;
          // These endpoints answer status:false when the list is empty.
          const data = response.data.status ? response.data.data || [] : [];
          if (clientFilter) {
            this.allItems = data.filter(clientFilter);
            this.totalData = this.allItems.length;
            this.pages = Math.ceil(this.totalData / PAGE_SIZE);
            this.showPage();
            return;
          }
          this.list = data;
          this.totalData = response.data.totalLength || this.list.length;
          this.pages = Math.ceil(this.totalData / PAGE_SIZE);
        })
        .catch((err) => {
          this.loading = false;
          this.list = [];
          this.allItems = [];
          console.log(err);
        });
    },
    // The current page of the applications filtered in getList
    showPage() {
      const start = (this.currentPage - 1) * PAGE_SIZE;
      this.list = this.allItems.slice(start, start + PAGE_SIZE);
    },
    fileUrl(key) {
      return this.baseURL + "file/get/" + key;
    },
    hasDocs(item) {
      return !!(item.proceedings || item.license) && item.status !== "Cancelled";
    },
    formatDateTime(value) {
      return value ? moment(value).format("D MMM YYYY, hh:mm A") : "";
    },
    view(item) {
      this.$router.push({ path: "/detailedviewapplicant", query: { id: item._id } });
    },
    pdf(url, id) {
      openPdf(axios, url, id).catch(() => this.toast("Could not open the order."));
    },

    // ── Extension ──
    openExtend(item) {
      this.extendApp = item;
      this.extendRows = (item.filmShootingDetails || []).map((d) => ({
        location: d.location || "",
        filmName: d.filmName || "",
        fromDate: d.fromDate ? moment(d.fromDate).format("YYYY-MM-DD") : "",
        toDate: d.toDate ? moment(d.toDate).format("YYYY-MM-DD") : "",
        noOfMembers: d.noOfMembers || "",
        noOfVehicles: d.noOfVehicles || "",
        installationDays: d.installationDays || "",
        menuStart: false,
        menuEnd: false,
      }));
      if (!this.extendRows.length) this.addExtendRow();
      this.extendDialog = true;
    },
    addExtendRow() {
      this.extendRows.push({ location: "", filmName: "", fromDate: "", toDate: "", noOfMembers: "", noOfVehicles: "", installationDays: "", menuStart: false, menuEnd: false });
    },
    submitExtend() {
      const missing = this.extendRows.find((r) => !r.location || !r.fromDate || !r.toDate);
      if (missing) {
        this.toast("Please fill the location and dates for every row.");
        return;
      }
      const originalEnd = (this.extendApp.filmShootingDetails || []).reduce((m, d) => (d.toDate && moment(d.toDate).isAfter(m) ? moment(d.toDate) : m), moment(0));
      const newEnd = this.extendRows.reduce((m, r) => (moment(r.toDate).isAfter(m) ? moment(r.toDate) : m), moment(0));
      if (!newEnd.isAfter(originalEnd, "day")) {
        this.toast("Extension dates must go beyond the original end date (" + originalEnd.format("DD MMM YYYY") + ").");
        return;
      }
      const form = new FormData();
      form.append("filmShootingDetails", JSON.stringify(this.extendRows));
      form.append("appid", this.extendApp._id);
      this.busy = true;
      axios({ url: "/extend/shooting", method: "POST", headers: { token: localStorage.getItem("token") }, data: form })
        .then((response) => {
          this.busy = false;
          if (!response.data.status) {
            this.toast(response.data.msg);
            return;
          }
          this.extendDialog = false;
          this.$appAlert({ type: "success", title: "Extension requested", message: response.data.msg }).then(() => this.getList());
        })
        .catch((err) => {
          this.busy = false;
          this.toast("Could not submit the extension request.");
          console.log(err);
        });
    },

    filmName(item) {
      const first = item.filmShootingDetails && item.filmShootingDetails[0];
      return (first && first.filmName) || item.natureOfApplication || "Shooting application";
    },
    firstLocation(item) {
      const d = item.filmShootingDetails || [];
      if (!d.length) return "";
      return d.length > 1 ? d[0].location + " +" + (d.length - 1) + " more" : d[0].location;
    },
    dateRange(item) {
      const d = item.filmShootingDetails || [];
      if (!d.length || !d[0].fromDate) return "";
      const from = moment.min(d.filter((x) => x.fromDate).map((x) => moment(x.fromDate)));
      const to = moment.max(d.filter((x) => x.toDate).map((x) => moment(x.toDate)));
      return from.format("D MMM") + " – " + to.format("D MMM YYYY");
    },
    money(value) {
      return Number(value || 0).toLocaleString("en-IN");
    },
    formatDate(value) {
      return value ? moment(value).format("D MMM YYYY") : "—";
    },
  },
};
</script>

<style scoped>
.list-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
}

.list-toolbar .ap-tabs {
  margin-bottom: 0;
}

.list-toolbar-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.list-filters {
  display: grid;
  grid-template-columns: 2fr 1fr 1fr;
  gap: 12px;
  padding-top: 18px;
}

.list-card {
  overflow: hidden;
}

.list-scroll {
  width: 100%;
  overflow-x: auto;
}

.list-table {
  width: 100%;
  border-collapse: collapse;
  font-family: "Inter", sans-serif;
}

.list-table thead th {
  padding: 16px 18px;
  background: #f6faf7;
  border-bottom: 1px solid var(--ap-line);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 1.4px;
  text-transform: uppercase;
  text-align: left;
  white-space: nowrap;
  color: var(--ap-muted);
}

.list-table thead th:first-child {
  border-top-left-radius: 22px;
}

.list-table thead th:last-child {
  border-top-right-radius: 22px;
}

.list-table tbody td {
  padding: 16px 18px;
  border-bottom: 1px solid var(--ap-line);
  vertical-align: middle;
  font-size: 14px;
  color: var(--ap-ink);
}

.list-table tbody tr:last-child td {
  border-bottom: none;
}

.list-table tbody tr {
  transition: background 0.2s ease;
}

.list-table tbody tr:hover {
  background: #f8fcf9;
}

.list-no {
  font-size: 12.5px;
  font-weight: 700;
  letter-spacing: 1px;
  color: var(--ap-gold);
  white-space: nowrap;
}

.list-title {
  font-family: "Inter", sans-serif;
  font-size: 17px;
  font-weight: 500;
  line-height: 1.3;
}

.list-strong {
  font-weight: 600;
}

.list-sub {
  margin-top: 2px;
  font-size: 12.5px;
  color: var(--ap-muted);
}

.list-ref {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-top: 4px;
  padding: 2px 8px;
  border-radius: 999px;
  background: #fdf8ee;
  font-size: 11.5px;
  color: #8a6a32;
}

.list-ref .v-icon {
  color: inherit !important;
}

.list-num {
  text-align: right;
  white-space: nowrap;
}

.list-fee {
  font-size: 15px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}

/* How a rejected or licensed application ended */
.outcome {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  min-width: 200px;
  max-width: 260px;
}

.outcome-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  flex-shrink: 0;
  border-radius: 11px;
  background: #dcfce7;
}

.outcome-icon .v-icon {
  color: #15803d !important;
}

.outcome--rejected .outcome-icon {
  background: #fee2e2;
}

.outcome--rejected .outcome-icon .v-icon {
  color: #b91c1c !important;
}

.outcome-title {
  font-size: 14px;
  font-weight: 700;
  line-height: 1.3;
  color: var(--ap-ink);
}

.outcome-when {
  margin-top: 1px;
  font-size: 12.5px;
  font-variant-numeric: lining-nums;
  color: var(--ap-muted);
}

.outcome-note {
  display: -webkit-box;
  margin-top: 4px;
  overflow: hidden;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  font-size: 12.5px;
  line-height: 1.45;
  color: #475569;
}

.outcome--rejected .outcome-note {
  color: #b91c1c;
}

.outcome-flag {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  margin-top: 6px;
  padding: 2px 9px;
  border-radius: 999px;
  background: #fef3c7;
  font-size: 11.5px;
  font-weight: 600;
  color: #92400e;
}

.outcome-flag .v-icon {
  color: inherit !important;
}

/* Where the application is now */
.where {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  min-width: 190px;
}

.where-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  flex-shrink: 0;
  border-radius: 11px;
  background: #ecfdf3;
}

.where-icon .v-icon {
  color: #15803d !important;
}

.where-who {
  font-size: 14px;
  font-weight: 700;
  line-height: 1.3;
  color: var(--ap-ink);
}

.where-detail {
  margin-top: 2px;
  font-size: 12.5px;
  line-height: 1.4;
  color: var(--ap-muted);
}

.where--you .where-icon {
  background: #fef3c7;
}

.where--you .where-icon .v-icon {
  color: #a16207 !important;
}

.where--range .where-icon {
  background: #e0f2fe;
}

.where--range .where-icon .v-icon {
  color: #0369a1 !important;
}

.where--closed .where-icon {
  background: #f1f5f9;
}

.where--closed .where-icon .v-icon {
  color: #475569 !important;
}

.list-status {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
}

.list-note {
  display: -webkit-box;
  max-width: 220px;
  overflow: hidden;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  font-size: 12px;
  line-height: 1.4;
}

.list-note--warn {
  color: #92400e;
}

.list-note--danger {
  color: #b91c1c;
}

.list-actions-head {
  text-align: right !important;
}

.list-actions-row {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  flex-wrap: nowrap;
}

/* Licence and proceedings downloads under the row's buttons */
.list-docs {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 6px;
  margin-top: 8px;
}

.list-doc {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  border-radius: 999px;
  border: 1px solid rgba(184, 149, 90, 0.5);
  background: #fdfbf6;
  font-size: 12px;
  font-weight: 600;
  color: #6f5527 !important;
  text-decoration: none;
  white-space: nowrap;
  transition: background 0.2s ease;
}

.list-doc:hover,
.list-doc:focus-visible {
  background: #f8f0dd;
  outline: none;
}

.list-doc .v-icon {
  color: inherit !important;
}

.list-icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  flex-shrink: 0;
  border-radius: 50%;
  border: 1px solid var(--ap-line);
  background: #ffffff;
  cursor: pointer;
  transition: border-color 0.2s ease, background 0.2s ease;
}

.list-icon-btn .v-icon {
  color: #14532d !important;
}

.list-icon-btn:hover {
  border-color: rgba(22, 163, 74, 0.45);
  background: #f4fbf7;
}

.list-pager {
  display: flex;
  justify-content: center;
  margin-top: 20px;
}

/* Phones and small tablets: each row becomes a labelled card */
@media (max-width: 900px) {
  .list-table thead {
    display: none;
  }

  .list-table,
  .list-table tbody,
  .list-table tr,
  .list-table td {
    display: block;
    width: 100%;
  }

  .list-table tbody {
    padding: 12px;
    background: #f8fbf9;
  }

  .list-table tbody tr {
    margin-bottom: 12px;
    padding: 6px 0;
    border-radius: 16px;
    border: 1px solid var(--ap-line);
    background: #ffffff;
  }

  .list-table tbody tr:last-child {
    margin-bottom: 0;
  }

  .list-table tbody td {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    padding: 9px 14px;
    border-bottom: 1px dashed var(--ap-line);
    text-align: right;
  }

  .list-table tbody td[data-label]::before {
    content: attr(data-label);
    flex-shrink: 0;
    padding-top: 2px;
    font-size: 10.5px;
    font-weight: 700;
    letter-spacing: 1.2px;
    text-transform: uppercase;
    text-align: left;
    color: var(--ap-muted);
  }

  .list-table tbody td.list-actions {
    border-bottom: none;
  }

  .list-actions-row {
    flex-wrap: wrap;
    width: 100%;
  }

  .list-docs {
    justify-content: flex-start;
    width: 100%;
  }

  .list-status {
    align-items: flex-end;
  }

  .where,
  .outcome {
    min-width: 0;
    max-width: none;
    flex-direction: row-reverse;
    text-align: right;
  }

  .outcome-flag {
    align-self: flex-end;
  }
}

@media (max-width: 600px) {
  .list-filters {
    grid-template-columns: 1fr;
  }
}
</style>
